import React, { useState, useMemo, useCallback } from 'react';
import {
  LTButton,
  LTInputBox,
  LTModal,
  LTSelect,
  LTText,
  LTTextarea,
  LTTooltip
} from '@lambdatestincprivate/lt-components';
import {
  FAILURE_OPTIONS,
  CUSTOM_OPTION,
  RCA_FEEDBACK_MESSAGES
} from './constants';
import { RCA_EVENTS } from './events';
import { truncateTextFromMiddle } from '@lambdatestincprivate/lt-components/lib/utils/functions';
import { SyncIcon } from '@primer/octicons-react';
import ThumbsUpIcon from '../../assets/ThumbsUpIcon';
import ThumbsDownIcon from '../../assets/ThumbsDownIcon';
import ChevronRightFilledIcon from '../../assets/ChevronRightFilledIcon';
import SuccessCheckIcon from '../../assets/SuccessCheckIcon';
import styles from './AIRca.module.css';
import { Portal } from '@primer/react';

const FeedbackSuccess = ({ message }) => (
  <div className={styles.successRow}>
    <div className={styles.successIcon}>
      <SuccessCheckIcon />
    </div>
    <LTText
      color="text.default"
      text={message}
      variant={LTText.TextVariant.SUBHEADER_REGULAR}
    />
  </div>
);

const RCAFeedback = ({
  testId,
  product,
  prefilledFeedback,
  feedbackProvided = false,
  onSubmitFeedback,
  onRefetch,
  onRefetchRCAData,
  onRetry,
  onTrack
}) => {
  const [feedback, setFeedback] = useState(null);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [lastFeedbackAction, setLastFeedbackAction] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    summary: '',
    customRootCause: '',
    additionalFeedback: ''
  });
  const [selectedFailure, setSelectedFailure] = useState(null);
  const [selectedRootCause, setSelectedRootCause] = useState(null);

  const rootCauseOptions = useMemo(() => {
    if (prefilledFeedback?.rootCause) {
      return [
        {
          name: prefilledFeedback.rootCause,
          value: prefilledFeedback.rootCause.toLowerCase()
        },
        CUSTOM_OPTION
      ];
    }
    return [CUSTOM_OPTION];
  }, [prefilledFeedback?.rootCause]);

  const isFeedbackOverLimit = formData?.additionalFeedback?.length > 500;

  const isSubmitDisabled = useMemo(() => {
    if (!selectedFailure || !selectedRootCause || !formData.summary.trim()) {
      return true;
    }
    if (
      selectedRootCause.value === 'custom' &&
      !formData.customRootCause.trim()
    ) {
      return true;
    }
    if (isFeedbackOverLimit) {
      return true;
    }
    return false;
  }, [
    selectedFailure,
    selectedRootCause,
    formData.summary,
    formData.customRootCause,
    isFeedbackOverLimit
  ]);

  const handleSuggestClick = useCallback(() => {
    setShowSuggestModal(true);
    if (prefilledFeedback) {
      if (prefilledFeedback.summary) {
        setFormData((prev) => ({
          ...prev,
          summary: prefilledFeedback.summary
        }));
      }
      if (prefilledFeedback.additional_feedback) {
        setFormData((prev) => ({
          ...prev,
          additionalFeedback: prefilledFeedback.additional_feedback
        }));
      }
      if (prefilledFeedback.failure) {
        const failureValue = String(prefilledFeedback.failure).toLowerCase();
        const matchedFailure = FAILURE_OPTIONS.find((option) => {
          const optionValue = option.value.toLowerCase();
          const optionName = option.name.toLowerCase();
          return optionValue === failureValue || optionName === failureValue;
        });
        if (matchedFailure) setSelectedFailure(matchedFailure);
      }
      if (prefilledFeedback.rootCause) {
        setSelectedRootCause({
          name: prefilledFeedback.rootCause,
          value: String(prefilledFeedback.rootCause).toLowerCase()
        });
      }
    }
  }, [prefilledFeedback]);

  const handleCloseModal = useCallback(() => {
    setShowSuggestModal(false);
    setFormData({ summary: '', customRootCause: '', additionalFeedback: '' });
  }, []);

  const handleThumbsUp = useCallback(async () => {
    if (!testId) return;
    setLastFeedbackAction('thumbs_up');
    onTrack?.(RCA_EVENTS.FEEDBACK_RATING, { rating: 'helpful' });
    try {
      await onSubmitFeedback?.({ rating: 10 });
      setFeedback('helpful');
      onRefetch?.();
    } catch {
      // feedback is non-critical
    }
  }, [testId, onSubmitFeedback, onRefetch, onTrack]);

  const handleThumbsDown = useCallback(async () => {
    if (!testId) return;
    onTrack?.(RCA_EVENTS.FEEDBACK_RATING, { rating: 'not_helpful' });
    // Record the thumbs-down, then open the suggestion form so the user can
    // elaborate on what was wrong.
    try {
      await onSubmitFeedback?.({ rating: 1 });
      onRefetch?.();
    } catch {
      // feedback is non-critical
    }
    handleSuggestClick();
  }, [testId, onSubmitFeedback, onRefetch, handleSuggestClick, onTrack]);

  const handleSubmitSuggestion = useCallback(async () => {
    setIsSubmitting(true);
    const payload = {
      category:
        selectedRootCause?.value === 'custom'
          ? formData.customRootCause
          : selectedRootCause?.name,
      ...(selectedFailure?.value === 'unknown'
        ? {}
        : { parent_category: selectedFailure?.value }),
      summary: formData.summary,
      rating: 10,
      product,
      additional_feedback: formData.additionalFeedback || undefined
    };

    try {
      setLastFeedbackAction('form_submit');
      onTrack?.(RCA_EVENTS.SUGGESTION_SUBMITTED, {
        failure_category: selectedFailure?.value,
        root_cause_category: selectedRootCause?.value,
        has_custom_cause: selectedRootCause?.value === 'custom',
        has_additional_feedback: !!formData.additionalFeedback,
      });
      await onSubmitFeedback?.(payload);
      handleCloseModal();
      onRefetchRCAData?.();
    } catch {
      // feedback is non-critical
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedRootCause,
    selectedFailure,
    formData,
    product,
    onSubmitFeedback,
    handleCloseModal,
    onRefetchRCAData,
    onTrack
  ]);

  const renderFeedbackContent = () => {
    if (lastFeedbackAction === 'thumbs_up') {
      return <FeedbackSuccess message={RCA_FEEDBACK_MESSAGES.thumbsUp} />;
    }
    if (lastFeedbackAction === 'thumbs_down') {
      return <FeedbackSuccess message={RCA_FEEDBACK_MESSAGES.thumbsDown} />;
    }
    if (lastFeedbackAction === 'form_submit' || feedbackProvided) {
      return <FeedbackSuccess message={RCA_FEEDBACK_MESSAGES.formSubmit} />;
    }

    return (
      <>
        {feedback === null && (
          <div className={styles.feedbackPrompt}>
            <LTText
              color="text.muted"
              text="Was this helpful?"
              variant={LTText.TextVariant.SUBHEADER_REGULAR}
            />
            <div className={styles.thumbsRow}>
              <button
                className={styles.thumbBtn}
                onClick={handleThumbsUp}
                aria-label="Helpful"
                type="button"
              >
                <ThumbsUpIcon />
              </button>
              <button
                className={styles.thumbBtn}
                onClick={handleThumbsDown}
                aria-label="Not helpful"
                type="button"
              >
                <ThumbsDownIcon />
              </button>
            </div>
          </div>
        )}
        {(feedback === 'helpful' || feedback === 'not_helpful') &&
          !lastFeedbackAction && (
            <FeedbackSuccess
              message={
                feedback === 'helpful'
                  ? RCA_FEEDBACK_MESSAGES.thumbsUp
                  : RCA_FEEDBACK_MESSAGES.thumbsDown
              }
            />
          )}
      </>
    );
  };

  return (
    <>
      <div className={styles.feedbackBar}>
        {renderFeedbackContent()}
        <div className={styles.feedbackActions}>
          {onRetry && (
            <LTButton
              variant={LTButton.Variant.DEFAULT}
              size={LTButton.Size.MEDIUM}
              leadingIcon={<SyncIcon />}
              onClick={onRetry}
            >
              Retry analysis
            </LTButton>
          )}
          <button
            className={styles.suggestLink}
            onClick={handleSuggestClick}
            type="button"
          >
            Suggest Improvements
          </button>
        </div>
      </div>

      {showSuggestModal && (
        <Portal>
          <LTModal
            noPortal
            title="Please Suggest How We Can Improve"
            onClose={handleCloseModal}
            extraStyle={{ width: '624px' }}
            renderFooter={
              <div className={styles.modalFooter}>
                <LTTooltip
                  text={
                    isSubmitDisabled || isSubmitting
                      ? 'All fields are required'
                      : ''
                  }
                  direction="top"
                >
                  <LTButton
                    variant={LTButton.Variant.PRIMARY}
                    size={LTButton.Size.MEDIUM}
                    onClick={handleSubmitSuggestion}
                    loading={isSubmitting}
                    disabled={isSubmitDisabled || isSubmitting}
                  >
                    Submit
                  </LTButton>
                </LTTooltip>
              </div>
            }
          >
            <div className={styles.modalBody}>
              <div className={styles.selectRow}>
                <div className={styles.selectHalf}>
                  <LTSelect
                    options={FAILURE_OPTIONS}
                    isMultiSelect={false}
                    defaultSelections={selectedFailure ? [selectedFailure] : []}
                    customButtonText={
                      <>
                        <span style={{ color: '#636C76' }}>Failure: </span>
                        <span style={{ color: '#24292F' }}>
                          {selectedFailure?.name || 'Select'}
                        </span>
                      </>
                    }
                    onChange={(values) => {
                      if (values?.[0]) setSelectedFailure(values[0]);
                    }}
                    buttonStyle={{
                      width: '100%',
                      '[data-component="buttonContent"]': {
                        justifyContent: 'start'
                      }
                    }}
                  />
                </div>
                <ChevronRightFilledIcon />
                <div className={styles.selectHalf}>
                  <LTSelect
                    options={rootCauseOptions}
                    isMultiSelect={false}
                    defaultSelections={
                      selectedRootCause ? [selectedRootCause] : []
                    }
                    customButtonText={
                      <>
                        <span style={{ color: '#636C76' }}>Root Cause: </span>
                        <span style={{ color: '#24292F' }}>
                          {selectedRootCause?.name
                            ? truncateTextFromMiddle({
                                textToTrim: selectedRootCause.name,
                                maxLength: 20
                              })
                            : 'Select'}
                        </span>
                      </>
                    }
                    onChange={(values) => {
                      if (values?.[0]) setSelectedRootCause(values[0]);
                    }}
                    buttonStyle={{
                      width: '100%',
                      '[data-component="buttonContent"]': {
                        justifyContent: 'start'
                      }
                    }}
                  />
                </div>
              </div>

              {selectedRootCause?.value === 'custom' && (
                <div className={styles.fieldGroup}>
                  <LTText
                    color="text.default"
                    variant={LTText.TextVariant.SUBHEADER_REGULAR}
                  >
                    Custom Root Cause <span className={styles.required}>*</span>
                  </LTText>
                  <LTInputBox
                    placeholder="Enter custom root cause"
                    ariaLabel="Custom Root Cause"
                    size={LTInputBox.Size.MEDIUM}
                    allowClearSearch={false}
                    value={formData.customRootCause}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        customRootCause: value
                      }))
                    }
                    inputOnly
                    variant={LTInputBox.Variant.WHITE}
                  />
                </div>
              )}

              <div className={styles.fieldGroup}>
                <LTText
                  color="text.default"
                  variant={LTText.TextVariant.SUBHEADER_REGULAR}
                >
                  Summary <span className={styles.required}>*</span>
                </LTText>
                <LTTextarea
                  id="rca-summary"
                  aria-label="Summary"
                  resize="none"
                  required
                  value={formData.summary}
                  placeholder="Enter a summary of the RCA"
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, summary: value }))
                  }
                  extraStyle={{ width: '100%' }}
                />
              </div>

              <div className={styles.fieldGroup}>
                <LTText
                  color="text.default"
                  variant={LTText.TextVariant.SUBHEADER_REGULAR}
                >
                  Additional Feedback
                </LTText>
                <LTTextarea
                  id="rca-additional-feedback"
                  aria-label="Additional Feedback"
                  resize="none"
                  placeholder="Enter any additional feedback"
                  value={formData.additionalFeedback}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      additionalFeedback:
                        typeof value === 'string'
                          ? value
                          : value?.target?.value || ''
                    }))
                  }
                  extraStyle={{ width: '100%' }}
                  maxLength={500}
                />
                <LTText
                  color={isFeedbackOverLimit ? 'danger.fg' : 'text.muted'}
                  variant={LTText.TextVariant.SMALL_REGULAR}
                  className={styles.charCount}
                >
                  {formData?.additionalFeedback?.length || 0}/500 characters
                  {isFeedbackOverLimit && ' — limit exceeded'}
                </LTText>
              </div>
            </div>
          </LTModal>
        </Portal>
      )}
    </>
  );
};

export default RCAFeedback;
